import { Card, Col } from 'antd'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../../../../components/custom_components/Modal'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import PerInputNumber from '../../../../components/custom_components/PerInputNumber'
import PerText from '../../../../components/custom_components/Text'
import { showNotification } from '../../../../components/custom_components/Notification/notification'
import { useAuth } from '../../../../context/auth-context'
import { updateSelectableNationalContractReferenceLevels } from '../../../../graphql/nationalContractReference/MutationFunctions'
import { NationalContractReferenceWithTypeDto, UpdateNationalContractReferenceLevelInput } from '../../../../types'
import { useAmountAndCurrency } from '../../../../shared/useAmountAndCurrency'

interface Props {
  visible: boolean
  reference: NationalContractReferenceWithTypeDto
  onClose: () => void
}

export const HrSupportNationalContractReferenceEditModal: FC<Props> = ({ visible, reference, onClose }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const [draftSalaries, setDraftSalaries] = useState<Record<string, number | undefined>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const errorMessageRef = useRef<HTMLDivElement>(null)

  const sortedLevels = useMemo(() => [...reference.levels].sort((a, b) => b.displayOrder - a.displayOrder), [reference.levels])

  useEffect(() => {
    if (!errorMessage || !errorMessageRef.current) {
      return
    }

    errorMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [errorMessage])

  useEffect(() => {
    if (visible) {
      setDraftSalaries({})
      setErrorMessage(undefined)
    }
  }, [visible, reference.type])

  const hasChanges = useMemo(
    () =>
      sortedLevels.some((level) => {
        const newValue = draftSalaries[level.level]
        return newValue !== undefined && newValue !== level.minSalary
      }),
    [draftSalaries, sortedLevels],
  )

  const handleClose = () => {
    onClose()
  }

  const handleSave = async () => {
    setIsSubmitting(true)
    setErrorMessage(undefined)

    const levels = sortedLevels.map((level) => ({
      level: level.level,
      minSalary: draftSalaries[level.level] ?? level.minSalary,
    }))

    const { errors } = await updateSelectableNationalContractReferenceLevels({
      type: reference.type,
      levels: levels as UpdateNationalContractReferenceLevelInput[],
    })

    setIsSubmitting(false)

    if (errors) {
      const errorMessage =
        errors[0]?.extensions?.validationErrors?.[0]?.message ||
        t('companySettingsView.nationalContractReferenceTab.editModal.errorMessage')
      setErrorMessage(errorMessage)
      return
    }

    showNotification({
      type: 'success',
      message: t('companySettingsView.nationalContractReferenceTab.editModal.notifications.success.title'),
      description: t('companySettingsView.nationalContractReferenceTab.editModal.notifications.success.description'),
    })
    handleClose()
  }

  return (
    <PerModal
      destroyOnClose
      size="small"
      title={{
        heading: t('companySettingsView.nationalContractReferenceTab.editModal.heading'),
        subheading: t('companySettingsView.nationalContractReferenceTab.editModal.subheading', {
          contractType: t(`employee.nationalContract.type.${reference.type}`),
        }),
      }}
      visible={visible}
      onCancel={handleClose}
      footer={{
        cancelBtn: { onClick: handleClose },
        confirmBtn: {
          text: 'save',
          onClick: handleSave,
          disabled: !hasChanges,
          isProcessing: { active: isSubmitting },
          tooltip: !hasChanges ? t('companySettingsView.nationalContractReferenceTab.editModal.saveDisabledTooltip') : '',
        },
      }}
    >
      <PerText fontSize="m" mb="m">
        {t('companySettingsView.nationalContractReferenceTab.editModal.emptyInputHint')}
      </PerText>

      <Card>
        <PerFlexRow>
          <Col span={5}>
            <PerText color="secondary" fontSize="s">
              {t('companySettingsView.nationalContractReferenceTab.editModal.columns.level')}
            </PerText>
          </Col>
          <Col span={10}>
            <PerText color="secondary" fontSize="s">
              {t('companySettingsView.nationalContractReferenceTab.editModal.columns.currentMinSalary')}
            </PerText>
          </Col>
          <Col span={9}>
            <PerText color="secondary" fontSize="s">
              {t('companySettingsView.nationalContractReferenceTab.editModal.columns.newMinSalary')}
            </PerText>
          </Col>
        </PerFlexRow>

        {sortedLevels.map((level) => (
          <PerFlexRow key={level.level} align="middle" mt="xs">
            <Col span={5}>
              <PerText fontSize="s">{t(`employee.nationalContract.level.${level.level}`)}</PerText>
            </Col>
            <Col span={10}>
              <PerText fontSize="s">{formatCurrency(level.minSalary, 'EUR', true)}</PerText>
            </Col>
            <Col span={9}>
              <PerInputNumber
                type="currency"
                currency="EUR"
                timezoneAndLocale={user}
                currencyPlaceholder
                min={0}
                step={1}
                width="100%"
                value={draftSalaries[level.level]}
                placeholder={t('companySettingsView.nationalContractReferenceTab.editModal.newValuePlaceholder')}
                onChange={(value) =>
                  setDraftSalaries((current) => ({
                    ...current,
                    [level.level]: !value && value !== 0 ? undefined : Number(value),
                  }))
                }
              />
            </Col>
          </PerFlexRow>
        ))}
      </Card>

      {errorMessage && (
        <div ref={errorMessageRef}>
          <PerText mt="m" color="error">
            {errorMessage}
          </PerText>
        </div>
      )}
    </PerModal>
  )
}
